import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';
import { showHeaderOverlayScrim, hideHeaderOverlayScrim } from '@theme/header-overlay-scrim';

/**
 * A custom element that manages a dialog.
 *
 * Two modes:
 * - default — a true modal, opened with `showModal()`, which puts the dialog in
 *   the top layer with its own backdrop and focus trap.
 * - `non-modal` attribute — opened with `show()`, so the dialog stays in the
 *   normal stacking context and the (sticky) header above it stays interactive.
 *   Add `overlay-scrim` alongside it to dim the page with the shared
 *   `#header-overlay-scrim` element, and to close on a click outside.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /** Whether the dialog opens non-modally (`show()` rather than `showModal()`). */
  get isNonModal() {
    return this.hasAttribute('non-modal');
  }

  /** Whether the dialog dims the page with the shared header overlay scrim. */
  get usesOverlayScrim() {
    return this.hasAttribute('overlay-scrim');
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /** Whether this dialog currently holds a reference on the shared scrim. */
  #scrimShown = false;

  /** @type {HTMLElement | null} Element focus returns to when a non-modal dialog closes. */
  #previouslyFocused = null;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    if (this.isNonModal) {
      // A non-modal dialog does not trap focus, so remember where focus came
      // from and hand it back on close.
      this.#previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;

      // Non-modal: the dialog stays in the normal stacking context, so the page
      // is held still with the `scroll-lock` attribute rather than by pinning
      // the body — pinning would reset the scroll position under an overlay
      // that is anchored to the header.
      dialog.show();
      document.documentElement.setAttribute('scroll-lock', '');

      if (this.usesOverlayScrim) {
        this.#scrimShown = true;
        showHeaderOverlayScrim();
      }

      this.dispatchEvent(new DialogOpenEvent());

      document.addEventListener('keydown', this.#handleKeyDown);

      // Deferred, so the click that opened the dialog does not close it again.
      queueMicrotask(() => {
        document.addEventListener('click', this.#handleDocumentClick);
      });

      return;
    }

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    document.removeEventListener('click', this.#handleDocumentClick);
    document.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    if (this.isNonModal) {
      if (this.#scrimShown) {
        this.#scrimShown = false;
        hideHeaderOverlayScrim();
      }
    } else {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    }

    dialog.close();
    dialog.classList.remove('dialog-closing');

    // Released after `close()` so this dialog is no longer counted, and only
    // when no other overlay still holds the page still.
    if (this.isNonModal) releaseScrollLock();

    if (this.isNonModal && this.#previouslyFocused?.isConnected) {
      this.#previouslyFocused.focus({ preventScroll: true });
      this.#previouslyFocused = null;
    }

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Closes a non-modal dialog when the click lands outside of it, including on
   * the shared header overlay scrim.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleDocumentClick = (event) => {
    const { dialog } = this.refs;

    if (!dialog.open) return;
    if (event.target instanceof Node && this.contains(event.target)) return;

    // The control that opens this dialog lives outside it (for example
    // `on:click="#search-modal/showDialog"` in the header). Clicking it again
    // must not be treated as a dismissal, or the open and close would race.
    if (this.id && event.target instanceof Element && event.target.closest(`[on\\:click^="#${this.id}/"]`)) {
      return;
    }

    this.closeDialog();
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * Bound as a class field because the non-modal path attaches it to `document`
   * as well as to the component itself.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown = (event) => {
    if (!this.refs.dialog?.open) return;
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  };

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

/**
 * Removes the document scroll lock unless another overlay still needs it.
 * Several overlays (search panel, cart drawer, header drawer) share the single
 * `scroll-lock` attribute on `<html>`, so the last one out turns off the light.
 */
export function releaseScrollLock() {
  if (document.querySelector('dialog[open][scroll-lock]')) return;
  if (document.querySelector('[data-drawer-open]')) return;

  document.documentElement.removeAttribute('scroll-lock');
}

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
